import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
  Table,
  CustomInput,
} from 'reactstrap';
import { AxiosAPI, getGroupID } from '../../../../../../helpers/AxiosAPI';
import { gAPI_Bulk_Form_Language_Version_Update, gAPI_Forms_List_Translate, gAPI_FormsList, gAPI_Internal_Property_By_Id } from '../../../../../../constants/APIs';
import Languages from '../../../../../../data/languages';

export default function AutoTranslateModal({
  isOpen,
  toggle,
  propertyId,
  formsList,
  languageCode,
  LoadData,
  propertyLanguages
}) {
  const [sourceLanguage, setSourceLanguage] = useState(languageCode || 'en');
  const [targetLanguage, setTargetLanguage] = useState('');
  const [step, setStep] = useState(1);
  const [previewData, setPreviewData] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingTarget, setLoadingTarget] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [translatingItems, setTranslatingItems] = useState({});
  const [translatedItems, setTranslatedItems] = useState({});
  // const [propertyLanguages, setPropertyLanguages] = useState([]);
  const [translateAllActive, setTranslateAllActive] = useState(false);
  const [targetFormLists, setTargetFormLists] = useState([]);

  const loadTargetForms = async (targetLang) => {

    if (isOpen && propertyId && propertyId !== '-1') {
      setLoadingTarget(true);
      await AxiosAPI({
        method: 'get',
        url: `${gAPI_FormsList}?groupId=${getGroupID()}&propertyId=${propertyId}&lang=${targetLang}`,
      })
        .then((res) => {
          const formIds = res.data.map((f) => f.id);
          const filtered = formsList.filter((f) => !formIds?.includes(f.id));
          setTargetFormLists([...res.data, ...filtered]);
          setLoadingTarget(false);
        }).catch((err) => {
          console.log('err', err);
          setLoadingTarget(false);
        });
    }
  }

  const translateSingleItem = async (id) => {
    const item = targetFormLists.find((f) => f.id === id);
    if (!item) return;

    setTranslatingItems((prev) => ({ ...prev, [id]: true }));

    try {
      const singleDataToSend = [
        {
          id: item.id,
          name: item.name || '',
          subTitle: item.subTitle || '',
          subject: item.subject || '',
          successMessage: item.successMessage || '',
          attachmentFiles: item.attachmentFiles || [],
          formContent: item.formContent || '',
        },
      ];

      const payload = {
        sourceLang: sourceLanguage,
        targetLang: targetLanguage,
        data: singleDataToSend,
      };

      const response = await AxiosAPI({
        method: 'POST',
        url: gAPI_Forms_List_Translate,
        data: payload,
      });

      const result = response.data;
      const translationsArray = Array.isArray(result) ? result : result.translations || [];
      const translationItem = translationsArray.find((t) => t.original.id == id);

      if (translationItem) {
        setPreviewData((prevData) => {
          return prevData.map((data) => {
            if (data.id === id) {
              return {
                ...data,
                translatedName: translationItem.translated?.name || '',
                translatedSubTitle: translationItem.translated?.subTitle || '',
                translatedSubject: translationItem.translated?.subject || '',
                translatedSuccessMessage: translationItem.translated?.successMessage || '',
                translatedAttachmentFiles: translationItem.translated?.attachmentFiles || [],
                translatedFormContent: translationItem.translated?.formContent || '',
              };
            }
            return data;
          });
        });
        setTranslatedItems((prev) => ({ ...prev, [id]: true }));
      }
    } catch (error) {
      console.error('Translation API error:', error);
    } finally {
      setTranslatingItems((prev) => ({ ...prev, [id]: false }));
    }
  };

  const handlePreview = () => {
    // Generate initial preview data structure without triggering API
    const initialPreviewData = targetFormLists.map((item) => {
      return {
        id: item.id,
        name: item.name || '',
        subTitle: item.subTitle || '',
        subject: item.subject || '',
        successMessage: item.successMessage || '',
        attachmentFiles: item.attachmentFiles || [],
        formContentRaw: item.formContent || '',
        languageCode: item.languageCode,

        // placeholders for translated text
        translatedName: '',
        translatedSubTitle: '',
        translatedSubject: '',
        translatedSuccessMessage: '',
        translatedAttachmentFiles: [],
        translatedFormContent: '',
      };
    });

    setPreviewData(initialPreviewData);
    setSelectedItems([]);
    setTranslatingItems({});
    setTranslatedItems({});
    setStep(2);
  };

  const handleToggleItem = (id) => {
    if (selectedItems.includes(id)) {
      setSelectedItems(selectedItems.filter((i) => i !== id));
    } else {
      setSelectedItems([...selectedItems, id]);
      if (!translatedItems[id] && !translatingItems[id]) {
        translateSingleItem(id);
      }
    }
  };

  const handleToggleAll = () => {
    if (selectedItems.length === previewData.length) {
      setSelectedItems([]);
    } else {
      const allIds = previewData.map((d) => d.id);
      setSelectedItems(allIds);
      allIds.forEach((id) => {
        if (!translatedItems[id] && !translatingItems[id]) {
          translateSingleItem(id);
        }
      });
    }
  };

  const handleTranslateAll = () => {
    setTranslateAllActive(true);
    previewData.forEach((data) => {
      if (!translatedItems[data.id] && !translatingItems[data.id]) {
        translateSingleItem(data.id);
      }
    });
  };

  // Auto-reset progress bar 2s after all translations finish
  useEffect(() => {
    if (translateAllActive && !Object.values(translatingItems).some(Boolean)) {
      const timer = setTimeout(() => setTranslateAllActive(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [translatingItems, translateAllActive]);

  const handleSave = async () => {
    try {
      setSaveLoading(true);
      const selectedData = selectedItems.map((id) =>
        previewData.find((d) => d.id === id)
      );

      const payload = {
        updates: selectedData.map((data) => ({
          id: data.id,
          languageCode: targetLanguage == 'zh-TW' ? 'hk' : targetLanguage,
          name: data.translatedName,
          subTitle: data.translatedSubTitle,
          subject: data.translatedSubject,
          successMessage: data.translatedSuccessMessage,
          attachmentFiles: data.translatedAttachmentFiles,
          formContent: data.translatedFormContent,
          updator: "dashboard"
        })),
      };

      // Call the bulk update API
      await AxiosAPI({
        method: 'POST',
        url: gAPI_Bulk_Form_Language_Version_Update,
        data: payload,
      });

      LoadData(languageCode);
      toggle();
      setStep(1);
      setSourceLanguage('');
      setSelectedItems([]);
      setPreviewData([]);
      setSaveLoading(false);
    } catch (error) {
      console.error('Translation save error:', error);
      setSaveLoading(false);
    }
  };

  // Helper for rendering table rows
  const renderTableRow = (
    data,
    fieldLabel,
    sourceValue,
    translatedValue,
    isFirst,
    isLast,
    rowSpanCount = 5,
    actionButton = null
  ) => {
    const topBorderStyle = isFirst ? {} : { borderTop: 'none' };
    const bottomBorderStyle = isLast ? {} : { borderBottom: 'none' };
    const style = {
      ...topBorderStyle,
      ...bottomBorderStyle,
      wordBreak: 'break-word',
      whiteSpace: 'pre-wrap',
    };
    const noVerticalBorder = {
      ...style,
      whiteSpace: fieldLabel === 'Status:' ? 'nowrap' : 'pre-wrap',
    };

    const isLargeContentField = fieldLabel === 'formContent:' || fieldLabel === 'Attachment Files:';

    const renderValue = (val) => {
      if (isLargeContentField && val) {
        return (
          <div style={{ maxHeight: '200px', overflowY: 'auto', paddingRight: '5px' }}>
            {val}
          </div>
        );
      }
      return val;
    };

    return (
      <tr key={`${data.id}-${fieldLabel}`}>
        {isFirst && (
          <td rowSpan={rowSpanCount} style={{ verticalAlign: 'middle', width: '50px' }}>
            <CustomInput
              type="checkbox"
              id={`translate-item-${data.id}`}
              label=""
              checked={selectedItems.includes(data.id)}
              onChange={() => handleToggleItem(data.id)}
            />
          </td>
        )}
        <td
          style={{
            ...topBorderStyle,
            ...bottomBorderStyle,
            whiteSpace: 'nowrap',
            width: '150px',
          }}
        >
          <strong>{fieldLabel}</strong>
        </td>
        <td style={noVerticalBorder}>
          {renderValue(sourceValue)}
        </td>
        <td style={noVerticalBorder}>
          {translatingItems[data.id] || loading ? (
            <div
              style={{
                width: '80%',
                minWidth: '100px',
                height: isLargeContentField ? '100px' : '18px',
                minHeight: '18px',
                display: 'block',
                backgroundColor: '#e0e0e0',
                borderRadius: '4px',
                animation: 'skeleton-pulse 1.5s infinite ease-in-out',
              }}
            >&nbsp;</div>
          ) : (
            <>
              {renderValue(translatedValue)}
              {isFirst && actionButton}
            </>
          )}
        </td>
        {isFirst && (
          <td rowSpan={rowSpanCount} style={{ verticalAlign: 'middle', width: '50px' }}>
            {data?.languageCode === targetLanguage ? (
              <span style={{ color: '#28a745', fontWeight: 'bold' }}>Available</span>
            ) : (
              <span style={{ color: '#dc3545', fontWeight: 'bold' }}>Unavailable</span>
            )}
          </td>
        )}
      </tr>
    );
  };

  const skeletonKeyframes = `
    @keyframes skeleton-pulse {
      0% { opacity: 1; }
      50% { opacity: 0.4; }
      100% { opacity: 1; }
    }
  `;

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl">
      <ModalHeader toggle={toggle}>Auto Translate</ModalHeader>
      <ModalBody
        style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}
      >
        <style>{skeletonKeyframes}</style>
        {step === 1 && (
          <>
            <FormGroup>
              <Label>Select Source Language</Label>
              <Input
                type="select"
                value={sourceLanguage}
                onChange={(e) => setSourceLanguage(e.target.value)}
                disabled={true}
              >
                <option value="">Select a language</option>
                {propertyLanguages &&
                  propertyLanguages.map((lang) => (
                    <option key={lang.code} value={lang.code}>
                      {lang.name || lang.code}
                    </option>
                  ))}
              </Input>
            </FormGroup>

            <FormGroup>
              <Label>Select Target Language</Label>
              <Input
                type="select"
                value={targetLanguage == 'hk' ? 'zh-TW' : targetLanguage}
                onChange={(e) => {
                  const normalizeLang = (lang) => (lang === 'zh-TW' ? 'hk' : lang);
                  const langValue = e.target.value;
                  setTargetLanguage(normalizeLang(langValue));
                  loadTargetForms(normalizeLang(langValue));
                }}
                disabled={loadingTarget}
              >
                <option value="">Select a language</option>
                {propertyLanguages &&
                  propertyLanguages?.filter((lang) => lang.code !== 'en').map((lang) => (
                    <option key={lang.code} value={lang.code}>
                      {lang.name || lang.code}
                    </option>
                  ))}
              </Input>
            </FormGroup>
          </>
        )}
        {step === 2 && translateAllActive && (() => {
          const translatedCount = Object.values(translatedItems).filter(Boolean).length;
          const total = previewData.length;
          const pct = total > 0 ? Math.round((translatedCount / total) * 100) : 0;
          const isAllDone = !Object.values(translatingItems).some(Boolean);
          return (
            <div style={{ marginBottom: '12px', padding: '10px 4px 4px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                <small style={{ color: isAllDone ? '#28a745' : '#555', fontWeight: 500 }}>
                  {isAllDone ? `✓ Translation complete — ${pct}%` : `Translating... ${pct}%`}
                </small>
                <small style={{ color: '#888' }}>{translatedCount} / {total} forms translated</small>
              </div>
              <div style={{ width: '100%', height: '7px', backgroundColor: '#e0e0e0', borderRadius: '4px', overflow: 'hidden' }}>
                <div
                  style={{
                    width: `${pct}%`,
                    height: '100%',
                    backgroundColor: isAllDone ? '#28a745' : 'var(--theme-color-1, #007bff)',
                    borderRadius: '4px',
                    transition: 'width 0.4s ease, background-color 0.4s ease',
                  }}
                />
              </div>
            </div>
          );
        })()}
        {step === 2 && (
          <Table responsive bordered>
            <thead>
              <tr>
                <th style={{ width: '50px' }}>
                  <CustomInput
                    type="checkbox"
                    id="selectAllTranslations"
                    label=""
                    checked={
                      selectedItems.length === previewData.length &&
                      previewData.length > 0
                    }
                    onChange={handleToggleAll}
                  />
                </th>
                <th style={{ width: '150px' }}>Field</th>
                <th style={{ width: '40%' }}>Current Text {`(${targetLanguage})`}</th>
                <th style={{ width: '40%' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>Translated Text {`(${targetLanguage})`}</span>
                    <Button
                      size="xs"
                      color="primary"
                      outline
                      onClick={handleTranslateAll}
                      disabled={Object.values(translatingItems).some(Boolean)}
                    >
                      Translate All
                    </Button>
                  </div>
                </th>
                <th style={{ width: '16%' }}>Language version {`(${targetLanguage})`}</th>
              </tr>
            </thead>
            <tbody>
              {previewData.length === 0 && (
                <tr>
                  <td colSpan="4" className="text-center">
                    No Forms Found
                  </td>
                </tr>
              )}
              {previewData.map((data) => {
                const attachmentStr = data.attachmentFiles && data.attachmentFiles.length > 0 ? JSON.stringify(data.attachmentFiles, null, 4) : '';
                const translatedAttachmentStr = data.translatedAttachmentFiles && data.translatedAttachmentFiles.length > 0 ? JSON.stringify(data.translatedAttachmentFiles, null, 4) : '';

                const rowSpanCount = attachmentStr ? 6 : 5;

                return (
                  <React.Fragment key={data.id}>
                    {renderTableRow(
                      data,
                      'Name:',
                      data.name,
                      data.translatedName,
                      true,
                      false,
                      rowSpanCount,
                      !translatedItems[data.id] && !translatingItems[data.id] ? (
                        <Button
                          size="xs"
                          color="link"
                          style={{ padding: 0, marginTop: '6px', fontSize: '12px' }}
                          onClick={() => translateSingleItem(data.id)}
                        >
                          {data?.languageCode === targetLanguage ? 'Re-Translate' : 'Translate'}
                        </Button>
                      ) : null
                    )}
                    {renderTableRow(
                      data,
                      'SubTitle:',
                      data.subTitle,
                      data.translatedSubTitle,
                      false,
                      false
                    )}
                    {renderTableRow(
                      data,
                      'Subject:',
                      data.subject,
                      data.translatedSubject,
                      false,
                      false
                    )}
                    {renderTableRow(
                      data,
                      'Success Message:',
                      data.successMessage,
                      data.translatedSuccessMessage,
                      false,
                      false
                    )}
                    {renderTableRow(
                      data,
                      'formContent:',
                      data.formContentRaw,
                      data.translatedFormContent,
                      false,
                      !attachmentStr
                    )}
                    {attachmentStr && renderTableRow(
                      data,
                      'Attachment Files:',
                      attachmentStr,
                      translatedAttachmentStr,
                      false,
                      true
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </Table>
        )}
      </ModalBody>
      <ModalFooter>
        {step === 1 ? (
          <Button
            color="primary"
            disabled={!sourceLanguage || loading || loadingTarget}
            onClick={handlePreview}
          >
            {loadingTarget ? 'Loading...' : loading ? 'Translating...' : 'Preview Translation'}
          </Button>
        ) : (
          <Button
            color="primary"
            disabled={selectedItems.length === 0 || loading || Object.values(translatingItems).some(Boolean) || saveLoading}
            onClick={handleSave}
          >
            {loading || Object.values(translatingItems).some(Boolean)
              ? 'Translating...'
              : saveLoading
                ? 'Saving...'
                : 'Save Translation'}
          </Button>
        )}
        <Button
          color="secondary"
          onClick={() => {
            toggle();
            setStep(1);
            setSourceLanguage('');
          }}
        >
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
}
